/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import { Input, InputGroup } from '@components'
import Button from '@components/Button'
import Modal from '@components/Modal'
import { successMessages } from '@constants/feedbackMessages'
import { Dialog } from '@headlessui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { UpdateSubSubjectRequest } from '@models/discipline.model'
import { addSubSubjectsSchema } from '@schemas/addSubSubject.schema'
import { disciplineApi } from '@services/DisciplineApi'
import { closeModal } from '@store/ducks/modal'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { inputIsValid } from '@utils/helpers'
import { getCssStyle, pixelsToRem } from '@utils/helpers/styleHelpers'

type Inputs = UpdateSubSubjectRequest

const styles = {
	container:
		'relative inline-block align-bottom bg-white rounded-lg pl-10 pr-10 pt-6 pb-9 text-left overflow-hidden shadow-xl transform transition-all my-8 sm:align-middle',
	title: 'text-lg leading-6 font-medium text-gray-900',
	subTitle: 'text-gray-500 mt-1',
	buttonGroup: 'mt-7 flex gap-4 justify-end',
}

export default function EditSubSubject() {
	const { subjectId } = useParams<{
		subjectId: string
	}>()

	const { modalInfo: id, modalName } = useAppSelector((state) => state.modal)

	const dispatch = useAppDispatch()

	const [getSubSubject, { data: subSubject }] =
		disciplineApi.useShowSubSubjectMutationMutation()

	const [editSubSubject, { isLoading, isSuccess }] =
		disciplineApi.useEditSubSubjectMutation()

	const {
		register,
		handleSubmit,
		setValue,
		formState: { errors, touchedFields },
	} = useForm<Inputs>({
		mode: 'onBlur',
		resolver: yupResolver(addSubSubjectsSchema),
	})

	useEffect(() => {
		if (id && modalName === 'editSubSubject') {
			getSubSubject({
				id: Number(id),
			})
		}
	}, [id, modalName, getSubSubject])

	const onSubmit: SubmitHandler<Inputs> = (data) => {
		editSubSubject({
			name: data.name,
			subjectId,
			id: id as number,
		})
	}

	useEffect(() => {
		if (isSuccess) {
			dispatch(closeModal())
			dispatch(notificationCreators.show(successMessages.updatedSubSubject))
		}
	}, [isSuccess, dispatch])

	useEffect(() => {
		if (subSubject) {
			setValue('name', subSubject?.name as string)
		}
	}, [subSubject, setValue])

	return (
		<Modal name='editSubSubject'>
			<Dialog.Panel
				style={getCssStyle(
					{ name: 'overflowY', value: 'auto' },
					{ name: 'maxWidth', value: pixelsToRem(630) },
					{ name: 'maxHeight', value: pixelsToRem(600) },
					{ name: 'width', value: '100%' }
				)}
				className={`${styles.container} modal-scroll`}
			>
				<p className={styles.title}>Editar Sub Assunto</p>
				<p className={styles.subTitle}>
					Preencha os campos abaixo para editar o sub assunto
				</p>

				<form onSubmit={handleSubmit(onSubmit)}>
					<InputGroup className='mt-4' label='Nome do sub assunto' id='name'>
						<Input
							isValid={inputIsValid(errors.name, touchedFields.name)}
							{...register('name')}
							type='text'
						/>
					</InputGroup>

					<div className={styles.buttonGroup}>
						<Button
							type='button'
							onClick={() => {
								dispatch(closeModal())
							}}
							variant='outline'
							disabled={false}
						>
							Cancelar
						</Button>

						<Button type='submit' isLoading={isLoading} disabled={!modalName}>
							Editar Sub Assunto
						</Button>
					</div>
				</form>
			</Dialog.Panel>
		</Modal>
	)
}
